/**
 * The wire shapes of the `subscriptions.*` action endpoints, and the vocabulary that keeps
 * client and server saying the same words. Mirrors KnowIt.Domain.Notifications.SubscriptionKinds.
 */

/** A subscribable thing's kind. Widen here and in SubscriptionKinds.cs together. */
export type SubscriptionKind =
  | "document"
  | "collection"
  | "flow"
  | "pulse:instrument"
  | "pulse:thread";

export interface SubscriptionStatus {
  kind: SubscriptionKind;
  targetId: string;
  event: string;
  subscribed: boolean;
  /**
   * Whether a producer actually publishes for this kind. A bell on an UNWIRED kind would
   * promise a notification that can never arrive, so the button refuses to render one.
   */
  wired: boolean;
  id?: string | null;
  channels?: string | null;
}

export interface SubscriptionRow {
  id: string;
  kind: SubscriptionKind;
  targetId: string;
  event: string;
  channels: string;
  createdAt: string;
  wired: boolean;
  /** Null when the target is gone or has no name — the UI shows the id, never a guess. */
  title: string | null;
}

export interface SubscriptionsPage {
  pagination: { offset: number; limit: number; total: number };
  data: SubscriptionRow[];
}

/** Where a subscribed thing lives. Kinds with no destination yet return null rather than a
 * link that 404s. The paths are the registry's route strings, so they hold in every host. */
export function targetHref(row: Pick<SubscriptionRow, "kind" | "targetId">): string | null {
  switch (row.kind) {
    case "document":
      return `/documents/${row.targetId}`;
    case "collection":
      return `/collections/${row.targetId}`;
    default:
      return null;
  }
}

export const KIND_LABEL: Record<SubscriptionKind, string> = {
  document: "Document",
  collection: "Collection",
  flow: "Flow",
  "pulse:instrument": "Instrument",
  "pulse:thread": "Storyline",
};

/** One cache key shape, so an invalidation cannot miss by spelling. */
export const subscribeKeys = {
  status: (kind: string, targetId: string) => ["subscription", kind, targetId] as const,
  mine: ["subscriptions", "mine"] as const,
};
